<script>
import ExperienceUi from "@/components/crew/experience/ExperienceUi.vue";
import ObjectMixin from "@/mixins/ObjectMixin";
import {permissionMixin} from "@/mixins/permissionMixins";
import {mapActions, mapGetters} from "vuex";
import {DateService} from "@/services/DateService";
import {AlertService} from "@/services/AlertService";
import {KeyService, crewErrorsHtml} from "@/services/KeyService";

export default {
  name: "RsGeneral",
  mixins: [ObjectMixin, permissionMixin],
  components: {ExperienceUi},
  data() {
    return {
      toEdit: false,
      mainReport: {},
      rankIdMaster: 1,
      rankIdCE: 2,
      crewLists: {},
    }
  },
  methods: {
    ...mapActions(['updateRsInfo']),
    async updateRs() {
      if (await AlertService.confirmUpdateAlert("Do you want to update ? ")) {
        const response = await this.updateRsInfo(this.mainReport);
        if (response) {
          AlertService.successAlert('Rightship updated successfully', 'UPDATE RIGHTSHIP')
          this.resetFields()
        }
      }
    },
    editMainReport() {
      this.toEdit = true;
      this.mainReport = {
        id: this.rsGet('id'),
        operator: this.rsGet('operator'),
        owners_contract_type: this.rsGet('owners_contract_type'),
        charterer: this.rsGet('charterer'),
      }
    },
    resetFields() {
      this.toEdit = false
      this.mainReport = {}
    },

    async getCrewExperienceLists() {
      const inspectionCommencementDate = this.rsGet('inspection_commencement_date')
      const vesselId = this.rsGet('vessel_id')
      const params = {
        onboard_from: inspectionCommencementDate,
        onboard_to: inspectionCommencementDate,
        vesselId: vesselId,
        hasImage: true,
      }
      swal.fire({
        'title': 'FETCHING CREW INFORMATION',
        allowEscapeKey: false,
        allowOutsideClick: false,
        allowEnterKey: false
      })
      swal.showLoading()
      const response = await KeyService.getCrewKeys(params)
      swal.close();
      if (response && !response.errors) {
        $("#crewExperienceInfo").modal('show');
        this.crewLists = response.crews
      } else if (!response || !response.cms_unavailable) {
        AlertService.errorAlert(crewErrorsHtml(response ? response.errors : null), 'FETCHING CREW INFORMATION')
      }
    },

    async cancelUpdate() {
      if (await AlertService.cancelAlert()) {
        this.resetFields()
      }
    }
  },
  computed: {
    ...mapGetters([
      'rsReport',
      'crewKeys',
      'manningFromOnboard',
      'manningCrewCount'
    ]),

    master() {
      return Object.values(this.crewKeys).filter((crew) => crew.rank_id === this.rankIdMaster);
    },
    chiefEngineer() {
      return Object.values(this.crewKeys).filter((crew) => crew.rank_id === this.rankIdCE);
    },

    formattedEventDate() {
      return DateService.dateEliteFormat(this.rsGet('inspection_commencement_date'))
    }
  },
}
</script>

<template>
  <div>
    <div class="card p-3 shadow-sm">
      <form @submit.prevent="">
        <div class="row justify-content-between">
          <div class="col-auto">
            <h5 class="e-text-red">
              General
            </h5>
          </div>
          <div class="col-auto">
            <template v-if="toEdit">
              <button class="e-btn e-btn-outlined-red ml-2" type="button" @click="cancelUpdate" v-if="toEdit">Cancel</button>
              <button class="e-btn e-btn-blue ml-2" type="button" @click="updateRs">Save</button>
            </template>
            <template v-else>
              <button
                class="e-btn e-btn-green ml-2 px-3"
                type="button"
                @click="editMainReport"
                v-if="isAllowed('rightship.main.general.edit') || user.id === rsReport.created_by_id || isElite() || isOwnVessel('rsReport')"
              >Edit General Details</button>
            </template>
          </div>
        </div>
        <table class="w-100">
          <tr>
            <td style="width: 25%">Owners Contract Type</td>

            <td>
              <input type="text" v-if="toEdit" v-model="mainReport.owners_contract_type" class="form-control-sm form-control"
                     name="owner-contract-type" id="owner-contract-type">
              <span v-else> {{ rsGet('owners_contract_type')  | toUpper }}</span>
            </td>
          </tr>
          <tr>
            <td>Charterer <sub class="lead e-text-red font-weight-bold">*</sub></td>
            <td>
              <input type="text" v-if="toEdit" v-model="mainReport.charterer" class="form-control-sm form-control" name="charterer"
                     id="charterer">
              <span v-else> {{ rsGet('charterer')  | toUpper }}</span>
            </td>
          </tr>
          <tr>
            <td style="width: 25%">Operator</td>
            <td>
              <input type="text" v-if="toEdit" v-model="mainReport.operator" class="form-control-sm form-control" name="operator"
                     id="operator">
              <span v-else> {{ rsGet('operator')  | toUpper }}</span>
            </td>
          </tr>
        </table>
        <hr/>
        <div class="row">
          <div class="col-12 text-right">
            <div class="mb-1">
              <button class="e-btn e-btn-blue ml-2"
                      type="button"
                      :disabled="crewKeys.length > 0"
                      data-toggle="modal"
                      data-target="#crewListInformation"
                      v-if="isAllowed('rightship.main.general.view.crew.info') || isElite()"
              >
                Crew List Information
              </button>
            </div>
            <div>
              <button class="e-btn e-btn-blue ml-2"
                      @click.prevent="getCrewExperienceLists" :disabled="crewKeys.length > 0"
                      type="button"
                      data-toggle="modal"
                      v-if="isAllowed('rightship.main.general.view.crew.exp') || isElite()">
              Crew Experience List
              </button>
            </div>
          </div>
        </div>
        <table class="w-100">
          <tr>
            <td style="width: 25%">Name of Master</td>
            <td>
              <p class="p-0 m-0" v-for="master in master">{{ master.last_name }}, {{ master.first_name }}
                {{ master.middle_name }}</p>
            </td>
          </tr>
          <tr>
            <td style="width: 25%">Name of C/E</td>
            <td>
              <p class="p-0 m-0" v-for="chiefEngineer in chiefEngineer">{{ chiefEngineer.last_name }}, {{ chiefEngineer.first_name }}
                {{ chiefEngineer.middle_name }}</p>
            </td>
          </tr>
          <tr>
            <td>Crew Numbers</td>
            <td>
              <p class="p-0 m-0" v-for="manningCount in manningCrewCount">{{ manningCount.name }} -
                <strong>{{ manningCount.count }}</strong></p>
            </td>
            <td>Total Crew Onboard:</td>
            <td><p class="p-0 m-0">{{ manningFromOnboard }}</p></td>
          </tr>
        </table>
        <div class="modal fade" id="crewListInformation" tabindex="-1" aria-labelledby="crewListInformationLabel" aria-hidden="true">
          <div class="modal-dialog modal-xl">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title" id="crewListInformationLabel">Crew List Information as of {{ formattedEventDate }}</h5>
              </div>
              <div class="modal-body">
                <table class="table table-bordered table-responsive-lg">
                  <thead>
                  <tr>
                    <th class="align-middle text-center" scope="col" style="width: 7%;">RANK</th>
                    <th class="align-middle text-center" style="width: 15%" scope="col">NAME</th>
                    <th class="align-middle text-center" style="width: 7%" scope="col">IPN</th>
                    <th class="align-middle text-center" style="width: 4%" scope="col">EMBARK DATE</th>
                    <th class="align-middle text-center" style="width: 10%" scope="col">TIME ONBOARD AT TIME OF REPORT</th>
                    <th class="align-middle text-center" style="width: 25%" scope="col">MANNING</th>
                    <th class="align-middle text-center" style="width: 5%" scope="col">NATIONALITY</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(crew,index) in crewKeys">
                    <td class="align-middle text-center">{{ crew.rank.toUpperCase() }}</td>
                    <td class="align-middle" style="white-space: nowrap">
                      <router-link :to="{name:'CrewInfo',params:{id:crew.id}}" target="_blank"
                                   v-if="isAllowed('rightship.main.general.view.crew.info-link')"
                      >
                        {{ crew.last_name }}, {{ crew.first_name }}
                        {{ crew.middle_name.substring(0, 1).toUpperCase() }}.
                      </router-link>
                      <span v-else>
                        {{ crew.last_name }}, {{ crew.first_name }}
                        {{ crew.middle_name.substring(0, 1).toUpperCase() }}.
                      </span>
                    </td>
                    <td class="align-middle text-center">{{ crew.ipn.toUpperCase() }}</td>
                    <td class="align-middle text-center">{{ crew.embarkationDate }}</td>
                    <!--                      <td class="align-middle text-center">{{crew.disembarkationDate}}</td>-->
                    <td class="align-middle text-center">{{ crew.duration.toUpperCase() }}</td>
                    <td class="align-middle text-center" style="white-space: nowrap">{{ crew.manning.toUpperCase() }}</td>
                    <td class="align-middle text-center">{{ crew.nationality.toUpperCase() }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-dismiss="modal">Close</button>
              </div>
            </div>
          </div>
        </div>
        <div class="modal fade" id="crewExperienceInfo" tabindex="-1" aria-labelledby="crewExperienceInfoLabel" aria-hidden="true">
          <div class="modal-dialog modal-xl">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title" id="crewListInformationLabel">Crew Experience List as of {{ formattedEventDate }}</h5>
                <!--              <button type="button" class="close" data-dismiss="modal" aria-label="Close">-->
                <!--                <span aria-hidden="true">&times;</span>-->
                <!--              </button>-->
              </div>
              <div class="modal-body" style="max-height:80vh; overflow-y: auto;">
                <template v-for="(crew,index) in crewLists">
                  <experience-ui :crew="crew"></experience-ui>
                </template>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-dismiss="modal">Close</button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
table td {
  padding: 2px !important;
}
</style>
